import { FeatureFlagIds } from "../../../utils/featureFlagIds";
import React from "react";
import {
  Typography,
  Grid,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Switch,
  FormControl,
} from "@mui/material";
import { useSelector } from "react-redux";
import { ICustomerEmployment } from "../../../Interfaces/Customer/ICustomerEmployment";
import { isValidDate, nullOrPositiveNumber } from "../../../utils/utils";
import { IJobCode } from "../../../Interfaces/Common/IJobCode";
import { IIncomePeriod } from "../../../Interfaces/Common/IIncomePeriod";
import { IIncomeType } from "../../../Interfaces/Common/IIncomeType";
import { IWorkShift } from "../../../Interfaces/Common/IWorkShift";
import StateSelector from "../../StateSelector";
import { AppState } from '../../../store/rootReducer';
import { useEmployeeRolePermissions } from "../../../Data/EmployeeRoleData";
import { ICustomerEmploymentStatus } from "../../../Interfaces/Common/ICustomerEmploymentStatus";
import { ICustomerEmploymentVerificationOption } from "../../../Interfaces/Common/ICustomerEmploymentVerificationOption";
import { IEmployee } from "../../../Interfaces/Admin/IEmployee";
import { useConfiguration } from "../../../Data/ConfigurationData";
import {getYearsAndMonthsElapsedLabelText, PhoneInput, DatePickerWithYearSanitizer} from "@amsanalytics/react-shared-library";
import styles from './_employment-info.module.scss';
import { isNotNil } from "ramda";
import {DatePickerChangeEvent} from "@progress/kendo-react-dateinputs";



interface EmploymentInfoProps {
  employmentInfo?: ICustomerEmployment;
  onUpdateEmployment(employment: ICustomerEmployment): void;
  errors?: Error[];
  newEmploymentDialog: boolean;
  customerEmploymentStatuses: ICustomerEmploymentStatus[];
  customerEmploymentVerificationOptions: ICustomerEmploymentVerificationOption[];
  disabled?: boolean;
  numberOfEmployments?: number;
  isReadOnly: boolean;
}

const EmploymentInfo: React.FC<EmploymentInfoProps> = (props) => {
  const employees: IEmployee[] = useSelector((state: AppState) => state.applications.employees);
  const activeUser: IEmployee = useSelector((state: AppState) => state.applications.loggedInUser!.employee);
  const [canView, canEdit, canAct] = useEmployeeRolePermissions();
  const jobCodes: IJobCode[] = useSelector(
    (state: AppState) => state.applications.jobCodes.sort((first, second) => first.sortOrder - second.sortOrder)
  );
  const { data: configuration } = useConfiguration();

  const incomePeriods: IIncomePeriod[] = useSelector(
    (state: AppState) => state.applications.incomePeriods
  );
  const incomeTypes: IIncomeType[] = useSelector(
    (state: AppState) => state.applications.incomeTypes.sort((first, second) => first.sortOrder - second.sortOrder)
  );
  const workshifts: IWorkShift[] = useSelector(
    (state: AppState) => state.applications.workShifts.sort((first, second) => first.sortOrder - second.sortOrder)
  );
  const currentApp = useSelector((state: AppState) => state.applications.editingApp!);

  const verifiedBy = employees.find(e => e.id === props.employmentInfo?.verifiedBy);

  // Whether or not we should disable the primary employment switch.
  const shouldDisablePrimaryEmploymentSwitch = (): boolean => {
    return props.newEmploymentDialog !== true && (isNotNil(props.numberOfEmployments) && props.numberOfEmployments === 1);
  };

  // Returns true if we should disable the years worked and months worked fields, false otherwise.
  const shouldDisableEmploymentTimeWorkedInputs = (): boolean => {
    return props.disabled || !props.employmentInfo?.isCurrent && isNotNil(props.employmentInfo?.startDate) || isNotNil(props.employmentInfo?.endDate);
  };

  const timeElapsedText = props.employmentInfo
    ? getYearsAndMonthsElapsedLabelText(props.employmentInfo.startDate, props.employmentInfo.endDate, props.employmentInfo.isCurrent ?? false, currentApp.applicationDate, props.employmentInfo.yearsWorked, props.employmentInfo.monthsWorked)
    : '';
  const timeElapsedLabelText = `Start Date ${timeElapsedText}`;

  return (
    <div className={props.isReadOnly ? styles.employmentInfoContainerDisabled : styles.employmentInfoContainer}>
      {props.employmentInfo ? (
        <Grid style={{flexGrow: 1, margin: 3}} container spacing={1}>
          <Grid item xs={12} sm={12} lg={12}>
            <Paper style={{padding: 'theme.spacing(1)'}}>
              <div style={props.employmentInfo?.primaryEmployment 
                  ? {
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: '#84eb88', // light green
                    borderRadius: '4px',
                    color: 'black',
                    marginBottom: '5px',
                    padding: '0 5px',
                  } 
                  : {
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '5px',
                    }}>
                <Typography variant="h6" component="h6">
                  {props.employmentInfo.primaryEmployment
                    ? "Primary " :
                    props.employmentInfo?.endDate
                      ? "Previous "
                      : "Other "}{" "}
                  Employment Details
                </Typography>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                  <Tooltip title={shouldDisablePrimaryEmploymentSwitch() ? `This is the customer's primary employment` : 'Primary Employment'}>
                    <FormControlLabel
                      label='Primary Employment'
                      control={
                        <Switch
                          size="small"
                          color="primary"
                          checked={props.employmentInfo.primaryEmployment}
                          onChange={((event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                            // Only update the primary employment if it's not disabled. We do this rather than disabling the switch
                            // so that the text and toggle colors are visible with the green background color.
                            if (!shouldDisablePrimaryEmploymentSwitch() && !props.disabled) {
                              props.onUpdateEmployment({
                                ...props.employmentInfo!,
                                primaryEmployment: checked
                              })
                            }
                          })}
                        />
                      }
                    />
                  </Tooltip>
                  <Tooltip title={props.employmentInfo.primaryEmployment === true ? 'The primary employment must be current' : 'Is the customer currently working here?'}>
                    <FormControlLabel
                      label="Current"
                      control={
                        <Checkbox
                          color="primary"
                          checked={props.employmentInfo.isCurrent}
                          style={{ marginLeft: '10px' }}
                          disabled={props.disabled} // If this is the primary employment, it must be current
                          onChange={((event) => {
                            if (props.disabled || props.employmentInfo?.primaryEmployment) {
                              return;
                            } else {
                              props.onUpdateEmployment({
                                ...props.employmentInfo!,
                                isCurrent: event.target.checked,
                                endDate: event.target.checked ? undefined : props.employmentInfo?.endDate
                              })
                            }
                          })}
                        />
                      }
                    />
                  </Tooltip>
                </div>
              </div>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    value={props.employmentInfo.companyName || ""}
                    onChange={(e) =>
                      props.onUpdateEmployment({
                        ...props.employmentInfo!,
                        companyName: e.currentTarget.value
                      })
                    }
                    fullWidth
                    label="Company Name"
                    error={props.errors !== undefined && props.errors.find((e) => e.name === 'CompanyName') !== undefined}
                    helperText={props.errors?.find((e) => e.name === 'CompanyName')?.message}
                    disabled={props.disabled}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    value={props.employmentInfo.title || ""}
                    onChange={(e) =>
                      props.onUpdateEmployment({
                        ...props.employmentInfo!,
                        title: e.currentTarget.value
                      })
                    }
                    fullWidth
                    label="Job Title/Occupation"
                    error={currentApp?.applicationQuotes?.[0]?.ruleViolations?.find(x => x.displayFieldId === 'jobtitle') !== undefined}
                    disabled={props.disabled}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    value={props.employmentInfo.supervisor ?? ""}
                    onChange={(e) =>
                      props.onUpdateEmployment({
                        ...props.employmentInfo!,
                        supervisor: e.currentTarget.value
                      })
                    }
                    fullWidth
                    label="Supervisor"
                    disabled={props.disabled}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    value={props.employmentInfo.streetAddress ?? ""}
                    onChange={(e) =>
                      props.onUpdateEmployment({
                        ...props.employmentInfo!,
                        streetAddress: e.currentTarget.value
                      })
                    }
                    fullWidth
                    label="Street"
                    error={props.errors !== undefined && props.errors.find((e) => e.name === 'Street') !== undefined}
                    helperText={props.errors?.find((e) => e.name === 'Street')?.message}
                    disabled={props.disabled}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    value={props.employmentInfo.city ?? ""}
                    onChange={(e) =>
                      props.onUpdateEmployment({
                        ...props.employmentInfo!,
                        city: e.currentTarget.value
                      })
                    }
                    fullWidth
                    label="City"
                    error={props.errors !== undefined && props.errors.find((e) => e.name === 'City') !== undefined}
                    helperText={props.errors?.find((e) => e.name === 'City')?.message}
                    disabled={props.disabled}
                  />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <StateSelector
                    showLabel
                    value={props.employmentInfo.stateId || ""}
                    onChange={(val) =>
                      props.onUpdateEmployment({
                        ...props.employmentInfo!,
                        stateId: val
                      })
                    }
                    DefaultStateId={props.employmentInfo.stateId}
                    error={props.errors !== undefined && props.errors.find((e) => e.name === 'State') !== undefined}
                    helperText={props.errors?.find((e) => e.name === 'State')?.message}
                    disabled={props.disabled}
                    fullWidth={true}
                  />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField
                    fullWidth
                    value={props.employmentInfo.zipCode || ""}
                    onChange={(e) =>
                      props.onUpdateEmployment({
                        ...props.employmentInfo!,
                        zipCode: e.currentTarget.value
                      })
                    }
                    label="Zip"
                    disabled={props.disabled}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    value={props.employmentInfo.hoursWorked || ""}
                    type="number"
                    onChange={(e) =>
                      props.onUpdateEmployment({
                        ...props.employmentInfo!,
                        hoursWorked: nullOrPositiveNumber(e.currentTarget.value) ? (nullOrPositiveNumber(e.currentTarget.value) as number) : 0
                      })
                    }
                    fullWidth
                    label="Hours/Week"
                    disabled={props.disabled}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    value={props.employmentInfo.hourlyPay || ""}
                    type="number"
                    onChange={(e) =>
                      props.onUpdateEmployment({
                        ...props.employmentInfo!,
                        hourlyPay: nullOrPositiveNumber(e.currentTarget.value) ? (nullOrPositiveNumber(e.currentTarget.value) as number) : undefined
                      })
                    }
                    fullWidth
                    label="Hourly Pay"
                    disabled={props.disabled}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={12} lg={12}>
            <Paper style={{ marginTop: 15, flex: 1, padding: 'theme.spacing(1)' }}>
              <Typography variant="h6" component="h6">
                Income Details
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={12}>
                  <TextField
                    value={props.employmentInfo.income || ""}
                    onChange={(e) =>
                      props.onUpdateEmployment({
                        ...props.employmentInfo!,
                        income: e.target.value ? (nullOrPositiveNumber(e.target.value) as number) : 0
                      })
                    }
                    type="number"
                    fullWidth
                    label="Monthly Income"
                    disabled={props.disabled}
                  />
                </Grid>
                {!configuration?.featureFlags?.[FeatureFlagIds.HideEmploymentType] &&
                  <Grid item xs={12} sm={4}>
                    <InputLabel>Employment Type</InputLabel>
                    <Select
                      name="employmentType"
                      disabled={props.disabled}
                      fullWidth
                      variant="standard"
                      value={props.employmentInfo.jobCodeId || ""}
                      onChange={(e) =>
                        props.onUpdateEmployment({
                          ...props.employmentInfo!,
                          jobCodeId: e.target.value as string
                        })
                      }
                    >
                      {jobCodes.map((x, key) => (
                        <MenuItem key={key} value={x.id}>
                          {x.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                }
                <Grid item xs={12} sm={4}>
                  <InputLabel>Work Shift</InputLabel>
                  <Select
                    name="workShift"
                    disabled={props.disabled}
                    fullWidth
                    variant="standard"
                    value={props.employmentInfo.workShiftId || ""}
                    onChange={(e) =>
                      props.onUpdateEmployment({
                        ...props.employmentInfo!,
                        workShiftId: e.target.value as string
                      })
                    }
                  >
                    {workshifts.map((x, key) => (
                      <MenuItem key={key} value={x.id}>
                        {x.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel>Income Period</InputLabel>
                  <Select
                    name="incomePeriod"
                    disabled={props.disabled}
                    fullWidth
                    variant="standard"
                    value={props.employmentInfo.incomePeriodId || ""}
                    onChange={(e) =>
                      props.onUpdateEmployment({
                        ...props.employmentInfo!,
                        incomePeriodId: e.target.value as string
                      })
                    }
                  >
                    <MenuItem key="null">(unassigned)</MenuItem>
                    {incomePeriods.map((x, key) => (
                      <MenuItem key={key} value={x.id}>
                        {x.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel>Employment Status</InputLabel>
                  <Select
                    name="employmentStatus"
                    disabled={props.disabled}
                    fullWidth
                    variant="standard"
                    value={props.employmentInfo.incomeTypeId || ""}
                    onChange={(e) =>
                      props.onUpdateEmployment({
                        ...props.employmentInfo!,
                        incomeTypeId: e.target.value as string
                      })
                    }
                  >
                    {incomeTypes.map((x, key) => (
                      <MenuItem key={key} value={x.id}>
                        {x.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={12} sm={4}>
                <FormControl variant="standard" fullWidth>
                  <InputLabel
                    shrink
                    style={{
                      fontSize: '21px',
                      color: 'rgba(0, 0, 0, 0.54)',
                      fontWeight: 400,
                      lineHeight: '1',
                      transform: 'translate(0, 1.5px) scale(0.75)',
                      transformOrigin: 'top left',
                      marginBottom: '2px'
                    }}
                  >
                    Phone
                  </InputLabel>

                  <div
                    style={{
                      marginTop: '18.5px',
                      borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
                      width: '100%'
                    }}
                  >
                    <PhoneInput
                      style={{
                        width: '100%',
                        border: 'none',
                        outline: 'none',
                        background: 'transparent'
                      }}
                      disabled={props.disabled}
                      onChangeValue={(val) =>
                        props.onUpdateEmployment({
                          ...props.employmentInfo!,
                          phone: val
                        })
                      }
                      value={props.employmentInfo.phone || ""}
                    />
                  </div>
                </FormControl>
              </Grid>
                {!configuration?.featureFlags?.[FeatureFlagIds.HideEmploymentType] &&
                  <Grid container item xs={12} sm={4} alignItems="flex-start"> </Grid>
                }
                <Grid container item xs={12} sm={2}>
                  <DatePickerWithYearSanitizer
                    disabled={props.disabled}
                    label={timeElapsedLabelText}
                    value={isValidDate(props.employmentInfo.startDate) ? new Date(props.employmentInfo.startDate!) : null}
                    onChange={(event: DatePickerChangeEvent) => {
                      props.onUpdateEmployment({
                        ...props.employmentInfo!,
                        startDate: event.value ?? undefined
                      });
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <DatePickerWithYearSanitizer
                    disabled={props.disabled || props.employmentInfo.isCurrent}
                    label="End Date"
                    value={isValidDate(props.employmentInfo.endDate) ? new Date(props.employmentInfo.endDate!) : null}
                    onChange={(event: DatePickerChangeEvent) => {
                      props.onUpdateEmployment({
                        ...props.employmentInfo!,
                        endDate: event.value ?? undefined
                      });
                    }}
                  />
                </Grid>
                {!configuration?.featureFlags?.[FeatureFlagIds.HideEmploymentType] &&
                  <Grid container item xs={12} sm={8} alignItems="flex-start"> </Grid>
                }
                <Grid item xs={12} sm={3}>
                  <InputLabel>
                    Verification Status {verifiedBy && props.employmentInfo.verifiedDate && !props.newEmploymentDialog &&
                      '(Set by ' +
                      `${verifiedBy.firstName} ${verifiedBy.lastName} ` +
                      `on ${new Date(props.employmentInfo.verifiedDate).toLocaleString()})`
                    }
                  </InputLabel>
                  <Select
                    name="employmentStatus"
                    disabled={!canAct("Actions", "Verify Employments")}
                    fullWidth
                    variant="standard"
                    value={props.employmentInfo.customerEmploymentStatusId ?? ""}
                    onChange={(e) =>
                      props.onUpdateEmployment({
                        ...props.employmentInfo!,
                        customerEmploymentStatusId: e.target.value as string,
                        verifiedDate: new Date(),
                        verifiedBy: activeUser.id
                      })
                    }
                  >
                    {props.customerEmploymentStatuses.map((ces, key) => (
                      <MenuItem key={key} value={ces.id}>
                        {ces.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <InputLabel>Verified With</InputLabel>
                  <Select
                    name="employmentVerificationOption"
                    disabled={!canAct("Actions", "Verify Employments")}
                    fullWidth
                    variant="standard"
                    value={props.employmentInfo.customerEmploymentVerificationOptionId ?? ""}
                    onChange={(e) =>
                      props.onUpdateEmployment({
                        ...props.employmentInfo!,
                        customerEmploymentVerificationOptionId: e.target.value as string
                      })
                    }
                  >
                    {props.customerEmploymentVerificationOptions.map((evo, key) => (
                      <MenuItem key={key} value={evo.id}>
                        {evo.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                {configuration?.featureFlags?.[FeatureFlagIds.WorkNumber] &&
                  <Grid item xs={12} sm={3}>
                    <TextField
                      value={props.employmentInfo.workNumber || ""}
                      onChange={(e) =>
                        props.onUpdateEmployment({
                          ...props.employmentInfo!,
                          workNumber: e.currentTarget.value
                        })
                      }
                      fullWidth
                      label="Work #"
                    />
                  </Grid>
                }
                <Grid item xs={12} sm={3} style={{ paddingTop: '13px' }}>
                  <TextField
                    value={props.employmentInfo.comment || ""}
                    onChange={(e) =>
                      props.onUpdateEmployment({
                        ...props.employmentInfo!,
                        comment: e.currentTarget.value
                      })
                    }
                    fullWidth
                    label="Comment"
                    multiline
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid >
      ) : null}
    </div >
  );
};

export default EmploymentInfo;
